import type { TFunction } from 'i18next';
import i18next from 'i18next';
import type { ProcessFlowBranch } from '@/@types/ProcessFlow';
import type { ExtendedUser } from '../../../@types/User';
import type { BranchHierachy } from '../../../data/branch/useBranchHierarchy';
import type { VehicleListResponseBucketKeys } from '../../../data/vehicles/useVehicles';
import type { StateIconTones } from '../../../styled-components/components/StateIcon';
import type { StatusStateProps } from '../../../styled-components/components/Status';
import { PrettyNumber } from '../../../utils/string-formatter';
import { filterConfigMap } from './filterConfig';

export interface NestedBucket {
  key: VehicleListResponseBucketKeys;
  buckets: TransformedBucket[];
}

export interface TransformedBucket {
  buckets?: NestedBucket[];
  query: string;
  checked: boolean;
  value: string;
  displayName?: string;
  count: number;
  state?: Record<string, StateIconTones>;
  status?: Record<string, StatusStateProps>;
  min?: number;
  max?: number;
  urlMin?: number;
  urlMax?: number;
  key: VehicleListResponseBucketKeys;
}

export const resolveBranch = (id: string, branchHierachy?: BranchHierachy) =>
  branchHierachy?.id === id
    ? branchHierachy.name
    : branchHierachy?.children.find((x) => x.id === id)?.name;

export const isSelected = (query: string, params: URLSearchParams): boolean => {
  const currentQueryAsParams = new URLSearchParams(query);
  let active = true;
  currentQueryAsParams.forEach((value, key) => {
    const exist = params.getAll(key).some((y) => y === value);
    if (!exist) {
      active = false;
    }
    return;
  });
  return active;
};

export const isRangeFilter = (key: VehicleListResponseBucketKeys) => {
  return filterConfigMap[key]?.type === 'range';
};

export const isSelectFilter = (key: VehicleListResponseBucketKeys) => {
  return filterConfigMap[key]?.type === 'select';
};

export const isManagedByFilter = (key: VehicleListResponseBucketKeys) => {
  return filterConfigMap[key]?.type === 'managedBy';
};

export const isBranchFilter = (key: VehicleListResponseBucketKeys) => {
  return filterConfigMap[key]?.type === 'branch';
};

export const getUnit = (t: TFunction, key: VehicleListResponseBucketKeys) => {
  const config = filterConfigMap[key];
  return t(config?.unit || '', { ns: 'glossary' });
};

export const formatRangeText = (
  t: TFunction,
  key: VehicleListResponseBucketKeys,
  params: URLSearchParams,
  min: number | undefined,
  prettyNumber?: boolean,
) => {
  const [urlMin, urlMax] = getMinMax(key, params);
  const config = filterConfigMap[key];

  let text = '';

  let unit = t(config?.unit || '', { ns: 'glossary' });

  if (!config.unitBefore) {
    unit = unit.toLocaleLowerCase();
  }

  const formattedMin = urlMin ? (prettyNumber ? PrettyNumber(urlMin) : urlMin) : undefined;
  const formattedMax = urlMax ? (prettyNumber ? PrettyNumber(urlMax) : urlMax) : undefined;

  if (formattedMin && formattedMax && config) {
    text = `${config.unitBefore ? unit : ''} ${formattedMin} - ${formattedMax} ${
      config.unitBefore ? '' : unit
    }`;
  } else if (formattedMin && config) {
    text = `${config.unitBefore ? `${unit} ` : ''}${formattedMin}+ ${
      config.unitBefore ? '' : unit
    }`;
  } else if (formattedMax && config) {
    text = `${config.unitBefore ? unit : ''} ${min} - ${formattedMax} ${
      config.unitBefore ? '' : unit
    }`;
  }

  return text;
};

const DoNotPrettify = ['vehicle.modelYear'];

export const getFilterText = (
  t: TFunction,
  transformedBuckets: TransformedBucket[],
  key: VehicleListResponseBucketKeys,
  params: URLSearchParams,
  users: ExtendedUser[] = [],
  branches: ProcessFlowBranch[] = [],
) => {
  const checked = transformedBuckets.filter((b) => b.checked);

  if (isManagedByFilter(key)) {
    const first = checked[0];

    if (!first) {
      return '';
    }

    const user = users?.find((u) => u.globalId === first.value);
    const displayName = user?.name || user?.email || first.value;
    return checked.length > 1 ? `${displayName} +${checked.length - 1}` : displayName;
  }

  if (isRangeFilter(key)) {
    const shouldPrettifyNumber = !DoNotPrettify.includes(key);
    return formatRangeText(t, key, params, transformedBuckets[0]?.min, shouldPrettifyNumber);
  }

  if (isBranchFilter(key)) {
    const first = checked[0];

    if (!first) {
      return '';
    }

    const branch = branches?.find((b) => b.id === first.value);
    const displayName = branch?.name || first.value;
    return checked.length > 1 ? `${displayName} +${checked.length - 1}` : displayName;
  }

  if (checked.length === 0) {
    return '';
  }

  const config = filterConfigMap[key];

  if (config && config.getLabelFromKey) {
    const label = config.getLabelFromKey(t, checked[0].value);
    if (checked.length > 1) {
      return `${label} +${checked.length - 1}`;
    }
    return label;
  }

  return checked.length > 1
    ? `${bucketValueTranslation(t, checked[0].value, key)} +${checked.length - 1}`
    : `${bucketValueTranslation(t, checked[0].value, key)}`;
};

export const getMinMax = (
  key: string,
  params: URLSearchParams,
): [number | undefined, number | undefined] => {
  const min = params.get(`${key}.min`);
  const max = params.get(`${key}.max`);
  return [min ? Number.parseInt(min, 10) : undefined, max ? Number.parseInt(max, 10) : undefined];
};

export const DefaultSort = 'updated-desc';

export const transformQuery = (query: string) => {
  const params = new URLSearchParams(query);
  params.delete('size');

  return params.toString();
};

export const cleanQuery = (query: string | URLSearchParams): URLSearchParams => {
  const queryUrlParams = new URLSearchParams(query);
  queryUrlParams.sort();
  queryUrlParams.delete('query');
  queryUrlParams.delete('offset');
  queryUrlParams.delete('size');
  queryUrlParams.delete('archived');
  queryUrlParams.delete('organizationId');

  return queryUrlParams;
};

export const useBucketTranslation = (t: TFunction, key: string) => {
  switch (key) {
    case 'hasContentDeclaration':
      return t('filter.hasContentDeclaration', { ns: 'salesAndPurchasingVehicles' });
    case 'station.displayName':
      return t('inventoryCellHeadings.location', { ns: 'salesAndPurchasingVehicles' });
    case 'visibility':
      return t('filter.visibility', { ns: 'salesAndPurchasingVehicles' });
    case 'tasks.displayName':
      return t('filter.processStep', { ns: 'salesAndPurchasingVehicles' });
    case 'tasks.process':
      return t('filter.process', { ns: 'salesAndPurchasingVehicles' });
    case 'tasks.state':
      return t('filter.processStatus', { ns: 'salesAndPurchasingVehicles' });
    case 'managedBy':
      return t('filter.managedBy', { ns: 'salesAndPurchasingVehicles' });
    case 'vehicle.manufacturer':
      return t('manufacturer', { ns: 'glossary' });
    case 'vehicle.modelYear':
      return t('modelYear', { ns: 'glossary' });
    case 'archived':
      return t('filter.archived', { ns: 'salesAndPurchasingVehicles' });
    case 'arrived':
      return t('filter.arrived', { ns: 'salesAndPurchasingVehicles' });
    case 'progressState.type':
      return t('filter.progress', { ns: 'salesAndPurchasingVehicles' });
    case 'purchaseInfo.type':
      return t('filter.purchaseInfo', { ns: 'salesAndPurchasingVehicles' });
    case 'purchased':
      return t('filter.purchased', { ns: 'salesAndPurchasingVehicles' });
    case 'ad.price':
      return t('filter.price', { ns: 'salesAndPurchasingVehicles' });
    case 'numberOfImages':
      return t('filter.numberOfImages', { ns: 'salesAndPurchasingVehicles' });
    case 'odometerValueAsKm':
      return t('filter.odometerValueKm', { ns: 'salesAndPurchasingVehicles' });
    case 'odometerValueAsScandinavianMiles':
      return t('filter.odometerValueScandinavianMiles', { ns: 'salesAndPurchasingVehicles' });
    case 'vehicle.colorName':
      return t('filter.colorName', { ns: 'salesAndPurchasingVehicles' });
    case 'vehicle.drivingWheel':
      return t('filter.drivingWheel', { ns: 'salesAndPurchasingVehicles' });
    case 'vehicle.gearboxType':
      return t('filter.gearboxType', { ns: 'salesAndPurchasingVehicles' });
    case 'vehicle.modelSeries':
      return t('filter.modelSeries', { ns: 'salesAndPurchasingVehicles' });
    case 'vehicle.segment':
      return t('filter.segment', { ns: 'salesAndPurchasingVehicles' });
    case 'branchId':
      return t('filter.branchId', { ns: 'salesAndPurchasingVehicles' });
    case 'enrichment':
      return t('filter.enrichment', { ns: 'salesAndPurchasingVehicles' });
    case 'insights.publishedDays':
      return t('filter.insights.publishedDays', { ns: 'salesAndPurchasingVehicles' });
    case 'insights.stockDays':
      return t('filter.insights.stockDays', { ns: 'salesAndPurchasingVehicles' });
    case 'vehicle.isAssociatedWithRisk':
      return t('filter.isAssociatedWithRisk', { ns: 'salesAndPurchasingVehicles' });
    case 'vehicle.fuelTypesString':
      return t('filter.fuelTypesString', { ns: 'salesAndPurchasingVehicles' });
    case 'vehicle.modelName':
      return t('filter.modelName', { ns: 'salesAndPurchasingVehicles' });
    case 'ad.hasManufacturerPackaging':
      return t('filter.hasManufacturerPackaging', { ns: 'salesAndPurchasingVehicles' });
    case 'ad.deductibleVat':
      return t('filter.deductibleVat', { ns: 'salesAndPurchasingVehicles' });
    case 'type':
      return t('filter.type', { ns: 'salesAndPurchasingVehicles' });
    case 'soldAt':
      return t('filter.soldAt', { ns: 'salesAndPurchasingVehicles' });
    case 'purchaseInfo.valuation':
      return t('filter.valuation', { ns: 'salesAndPurchasingVehicles' });
    case 'processes_tasks.process':
      return t('filter.processes', { ns: 'salesAndPurchasingVehicles' });
    case 'tasks_tasks.displayName':
      return t('filter.processSteps', { ns: 'salesAndPurchasingVehicles' });
    case 'erpName':
      return t('filter.erpName', { ns: 'salesAndPurchasingVehicles' });
    case 'finnPublication.state':
      return t('filter.finnState', { ns: 'salesAndPurchasingVehicles' });
    case 'blocketPublication.status':
      return t('filter.blocketStatus', { ns: 'salesAndPurchasingVehicles' });
    case 'internalPublication.state':
      return t('filter.internalPublicationState', { ns: 'salesAndPurchasingVehicles' });
    case 'status':
      return t('filter.status', { ns: 'salesAndPurchasingVehicles' });
    case 'lastUpdatedAt':
      return t('filter.lastUpdatedAt', { ns: 'salesAndPurchasingVehicles' });
    default:
      return key;
  }
};

// Values from process flow can be prefixed that needs to be replaced
const replaceAbleValues: string[] = [
  'processes_tasks.process_',
  'tasks_tasks.displayName_',
  'tasks.displayName_',
  'tasks.state_',
];

const replaceValue = (s: string) =>
  replaceAbleValues.reduce(
    (acc, value) => (acc.startsWith(value) ? acc.replace(value, '') : acc),
    s,
  );

export const bucketValueTranslation = (
  t: TFunction,
  value: string,
  key: VehicleListResponseBucketKeys,
) => {
  // Handle missing- prefix generically for all bucket types
  if (value.startsWith('missing-')) {
    const fieldName = value.replace('missing-', '');
    return t(`missing.${fieldName}`, {
      ns: 'salesAndPurchasingVehicles',
      defaultValue: t('missing.default', {
        ns: 'salesAndPurchasingVehicles',
      }),
    });
  }

  switch (key) {
    case 'archived':
      switch (value) {
        case 'true':
          return t('archived.archived', { ns: 'salesAndPurchasingVehicles' });
        case 'false':
          return t('archived.active', { ns: 'salesAndPurchasingVehicles' });
        default:
          break;
      }
      break;
    case 'status':
      return t(`vehicleStatus.${`${value[0].toLowerCase()}${value.slice(1)}`}`, {
        ns: 'salesAndPurchasingVehicles',
      });
    case 'internalPublication.state':
      return t(`internalPublication.state.${value.toLowerCase()}`, {
        ns: 'salesAndPurchasingVehicles',
      });
    case 'finnPublication.state':
      return t(`finnPublication.state.${value.toLowerCase()}`, {
        ns: 'salesAndPurchasingVehicles',
      });
    case 'blocketPublication.status':
      return t(`blocketPublication.status.${value.toLowerCase()}`, {
        ns: 'salesAndPurchasingVehicles',
      });
    default:
      break;
  }

  const trimmedValue = replaceValue(value);

  switch (trimmedValue) {
    case 'Completed':
    case 'Unsupported':
    case 'Pending':
    case 'Imported':
      return t(`enrichment.${trimmedValue.toLowerCase()}`, { ns: 'salesAndPurchasingVehicles' });
    case 'Depublished':
    case 'Unpublished':
    case 'Published':
      return t(`visibility.${trimmedValue.toLowerCase()}`, { ns: 'salesAndPurchasingVehicles' });
    case 'urn:pf:task:state:started':
      return t('vehicleProcessStatus.ongoing', { ns: 'salesAndPurchasingVehicles' });
    case 'urn:pf:task:state:not-applicable':
      return t('vehicleProcessStatus.not-applicable', { ns: 'salesAndPurchasingVehicles' });
    case 'urn:pf:task:state:completed':
      return t('vehicleProcessStatus.completed', { ns: 'salesAndPurchasingVehicles' });
    case 'urn:pf:task:state:failed':
      return t('vehicleProcessStatus.canceled', { ns: 'salesAndPurchasingVehicles' });
    case 'urn:pf:task:state:requires-approval':
      return t('vehicleProcessStatus.requiresApproval', { ns: 'salesAndPurchasingVehicles' });
    case 'urn:pf:task:state:pending':
      return t('vehicleProcessStatus.pending', { ns: 'salesAndPurchasingVehicles' });
    case 'urn:pf:task:state:initializing':
      return t('vehicleProcessStatus.initializing', { ns: 'salesAndPurchasingVehicles' });
    case 'urn:wayke:vehicle:purchase-type:build-to-order':
      return t('shared.purchaseInfo.types.buildToOrder', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:purchase-type:trade-in':
      return t('shared.purchaseInfo.types.tradeIn', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:purchase-type:workshop':
      return t('shared.purchaseInfo.types.workshop', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:purchase-type:rental':
      return t('shared.purchaseInfo.types.rental', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:purchase-type:leasing':
      return t('shared.purchaseInfo.types.leasing', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:purchase-type:import':
      return t('shared.purchaseInfo.types.import', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:purchase-type:brokerage':
      return t('shared.purchaseInfo.types.brokerage', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:purchase-type:demo':
      return t('shared.purchaseInfo.types.demo', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:purchase-type:direct-purchase':
      return t('shared.purchaseInfo.types.directPurchase', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:purchase-type:unknown':
      return t('shared.purchaseInfo.types.unknown', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:progress-state:pre-calculation':
      return t('shared.progress.types.preCalculation', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:progress-state:assessmen':
      return t('shared.progress.types.assessment', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:progress-state:purchase-calculation':
      return t('shared.progress.types.purchaseCalculation', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:progress-state:purchase-contract':
      return t('shared.progress.types.purchaseContract', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:progress-state:incoming':
      return t('shared.progress.types.incoming', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:progress-state:receiving-inspection':
      return t('shared.progress.types.receivingInspection', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:progress-state:workshop':
      return t('shared.progress.types.workshop', { ns: 'salesAndPurchasingVehiclesItem' });
    case 'urn:wayke:vehicle:progress-state:marketing':
      return t('shared.progress.types.marketing', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:progress-state:state-report':
      return t('shared.progress.types.stateReport', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:progress-state:self-declaration':
      return t('shared.progress.types.selfDeclaration', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:progress-state:planning':
      return t('shared.progress.types.planning', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:progress-state:auction':
      return t('shared.progress.types.auction', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:progress-state:idle':
      return t('shared.progress.types.idle', { ns: 'salesAndPurchasingVehiclesItem' });
    case 'urn:wayke:vehicle:progress-state:ready-for-sale':
      return t('shared.progress.types.readyForSale', {
        ns: 'salesAndPurchasingVehiclesItem',
      });
    case 'urn:wayke:vehicle:progress-state:failed':
      return t('shared.progress.types.failed', { ns: 'salesAndPurchasingVehiclesItem' });
    case 'urn:wayke:vehicle:progress-state:assessment':
      return t('shared.progress.types.assessment', { ns: 'salesAndPurchasingVehiclesItem' });
    case 'urn:wayke:vehicle:progress-state:sold':
      return t('shared.progress.types.sold', { ns: 'salesAndPurchasingVehiclesItem' });
    case '<30':
      return key === 'lastUpdatedAt'
        ? t('shared.lastUpdatedAt.<30', { ns: 'salesAndPurchasingVehiclesItem' })
        : t('shared.count.<30', { ns: 'salesAndPurchasingVehiclesItem' });
    case '30-60':
      return key === 'lastUpdatedAt'
        ? t('shared.lastUpdatedAt.30-60', { ns: 'salesAndPurchasingVehiclesItem' })
        : t('shared.count.30-60', { ns: 'salesAndPurchasingVehiclesItem' });
    case '60-90':
      return key === 'lastUpdatedAt'
        ? t('shared.lastUpdatedAt.60-90', { ns: 'salesAndPurchasingVehiclesItem' })
        : t('shared.count.60-90', { ns: 'salesAndPurchasingVehiclesItem' });
    case '>90':
      return t('shared.count.>90', { ns: 'salesAndPurchasingVehiclesItem' });
    case '90-120':
      return t('shared.lastUpdatedAt.90-120', { ns: 'salesAndPurchasingVehiclesItem' });
    case '>120':
      return t('shared.lastUpdatedAt.>120', { ns: 'salesAndPurchasingVehiclesItem' });
    case 'true':
      return t('yes', { ns: 'common' });
    case 'false':
      return t('no', { ns: 'common' });
    default:
      return trimmedValue;
  }
};

export interface Status {
  displayName: string;
  state: 'completed' | 'pending' | 'canceled';
}

export const Statuses: { [key: string]: Status | undefined } = {
  Published: {
    displayName: i18next.t('vehicleStatus.published', { ns: 'salesAndPurchasingVehicles' }),
    state: 'completed',
  },
  Unpublished: {
    displayName: i18next.t('vehicleStatus.unpublished', { ns: 'salesAndPurchasingVehicles' }),
    state: 'pending',
  },
  Enriched: {
    displayName: i18next.t('vehicleStatus.enriched', { ns: 'salesAndPurchasingVehicles' }),
    state: 'pending',
  },
  PartlyEnriched: {
    displayName: i18next.t('vehicleStatus.partlyEnriched', { ns: 'salesAndPurchasingVehicles' }),
    state: 'pending',
  },
  Sold: {
    displayName: i18next.t('vehicleStatus.sold', { ns: 'salesAndPurchasingVehicles' }),
    state: 'canceled',
  },
  Imported: {
    displayName: i18next.t('vehicleStatus.imported', { ns: 'salesAndPurchasingVehicles' }),
    state: 'pending',
  },
  Deleted: {
    displayName: i18next.t('vehicleStatus.deleted', { ns: 'salesAndPurchasingVehicles' }),
    state: 'canceled',
  },
  DeletedFromDms: {
    displayName: i18next.t('vehicleStatus.deletedFromDms', { ns: 'salesAndPurchasingVehicles' }),
    state: 'canceled',
  },
  Unsupported: {
    displayName: i18next.t('vehicleStatus.unsupported', { ns: 'salesAndPurchasingVehicles' }),
    state: 'canceled',
  },
};

export const cleanDMSName = (name?: string) =>
  name ? name.replaceAll('_', ' ').replaceAll('-', ' ') : '';
